/**
 * Pure decisions of the drag: which column the pointer would trade places
 * with, and what the candidate and source panels would look like. No three.js,
 * no model — unit-testable, and the only place the rules live.
 */

export interface DecisionGroup {
	id: string;
	x: number;
	width: number;
	locked: boolean;
	distributable: boolean;
	draggable: boolean;
}

export interface SwapTargetInput {
	hypotheticalX: number;
	draggedId: string;
	groups: Array<DecisionGroup>;
	previousSwapTargetId: string | null;
	enterFraction: number;
	exitFraction: number;
}

/**
 * The column the dragged one would swap with: its centre line over another
 * draggable column (within `enterFraction` of that column's width from its
 * centre — 0.5 is the column's full width). A current target is kept until
 * the centre leaves the slightly wider exit band, so the mode does not flicker
 * at the edge.
 */
export const decideSwapTargetCfn = (input: SwapTargetInput): string | null => {
	const { hypotheticalX, draggedId, groups, previousSwapTargetId, enterFraction, exitFraction } = input;
	const candidates = groups.filter((g) => g.id !== draggedId && g.draggable && g.width > 0);

	if (previousSwapTargetId) {
		const previous = candidates.find((g) => g.id === previousSwapTargetId);
		if (previous && Math.abs(hypotheticalX - previous.x) <= exitFraction * previous.width) {
			return previous.id;
		}
	}

	let best: DecisionGroup | null = null;
	let bestDistance = Infinity;
	for (const g of candidates) {
		const distance = Math.abs(hypotheticalX - g.x);
		if (distance <= enterFraction * g.width && distance < bestDistance) {
			best = g;
			bestDistance = distance;
		}
	}
	return best?.id ?? null;
};

export interface FineTuneRoomInput {
	groups: Array<DecisionGroup>;
	draggedId: string;
	panelWidth: number;
	overlap: { left: number; right: number };
}

export interface FineTuneRoom {
	/** nearest column on each side by CURRENT position; null at the panel's end */
	left: DecisionGroup | null;
	right: DecisionGroup | null;
	/** the facing edges: the neighbour's edge, or the usable run's end */
	leftEdge: number;
	rightEdge: number;
	/** the range the dragged column's centre may take without overlapping either */
	minX: number;
	maxX: number;
}

/**
 * The room a column has between its two neighbours (or the panel's usable
 * ends) — fine-tune mode moves it anywhere in there. Same arithmetic as the
 * library's `getFineTuneGapCfn`, so the preview and the commit agree.
 */
export const fineTuneRoomCfn = (input: FineTuneRoomInput): FineTuneRoom | null => {
	const dragged = input.groups.find((g) => g.id === input.draggedId);
	if (!dragged) return null;
	const others = input.groups.filter((g) => g.id !== input.draggedId && g.distributable && g.width > 0);
	const left = others.filter((g) => g.x < dragged.x).sort((a, b) => b.x - a.x)[0] ?? null;
	const right = others.filter((g) => g.x > dragged.x).sort((a, b) => a.x - b.x)[0] ?? null;
	const leftEdge = left ? left.x + (left.width / 2) : (-input.panelWidth / 2) + input.overlap.left;
	const rightEdge = right ? right.x - (right.width / 2) : (input.panelWidth / 2) - input.overlap.right;
	let minX = leftEdge + (dragged.width / 2);
	let maxX = rightEdge - (dragged.width / 2);
	if (minX > maxX) {
		const mid = (minX + maxX) / 2;
		minX = mid;
		maxX = mid;
	}
	return { left, right, leftEdge, rightEdge, minX, maxX };
};

export interface PlanGroup {
	id: string;
	width: number;
	x: number;
	locked: boolean;
	distributable: boolean;
}

export interface PreviewPlanRequest {
	mode: 'insert' | 'swap';
	sameIsland: boolean;
	hypotheticalX: number;
	dragged: { id: string; width: number; originalX: number };
	/** the candidate panel's columns (includes the dragged one when sameIsland) */
	candidateGroups: Array<DecisionGroup>;
	/** the source panel's columns (includes the dragged one) */
	sourceGroups: Array<DecisionGroup>;
	swapTargetId: string | null;
}

export interface PreviewPlanInputs {
	candidate: Array<PlanGroup>;
	/** null when the drag stays on its own island */
	source: Array<PlanGroup> | null;
}

const toPlan = (g: DecisionGroup, x = g.x): PlanGroup => ({ id: g.id, width: g.width, x, locked: g.locked, distributable: g.distributable });

/**
 * The columns each affected panel would hold, with the dragged column at its
 * hypothetical x (insert) or in the target's slot and the target in the
 * dragged one's old slot (swap). Feed each list to the library planner.
 */
export const buildPreviewPlanInputsCfn = (req: PreviewPlanRequest): PreviewPlanInputs => {
	const { mode, sameIsland, hypotheticalX, dragged, candidateGroups, sourceGroups, swapTargetId } = req;
	const target = swapTargetId ? candidateGroups.find((g) => g.id === swapTargetId) ?? null : null;
	const draggedPlan: PlanGroup = { id: dragged.id, width: dragged.width, x: hypotheticalX, locked: false, distributable: true };

	if (sameIsland) {
		if (mode === 'swap' && target) {
			return {
				candidate: candidateGroups.map((g) => {
					if (g.id === dragged.id) return toPlan(g, target.x);
					if (g.id === target.id) return toPlan(g, dragged.originalX);
					return toPlan(g);
				}),
				source: null
			};
		}
		return {
			candidate: candidateGroups.map((g) => (g.id === dragged.id ? { ...draggedPlan } : toPlan(g))),
			source: null
		};
	}

	const sourceWithout = sourceGroups.filter((g) => g.id !== dragged.id).map((g) => toPlan(g));

	if (mode === 'swap' && target) {
		return {
			candidate: candidateGroups.map((g) => (g.id === target.id ? { ...draggedPlan, x: target.x } : toPlan(g))),
			source: [...sourceWithout, { id: target.id, width: target.width, x: dragged.originalX, locked: false, distributable: true }]
		};
	}

	return {
		candidate: [...candidateGroups.map((g) => toPlan(g)), draggedPlan],
		source: sourceWithout
	};
};

/**
 * Widths an island's panel would carry after the drop — the input to the
 * growth prediction. Mirrors `buildPreviewPlanInputsCfn` for the width check.
 */
export const hypotheticalWidthsCfn = (req: PreviewPlanRequest): { candidate: Array<number>; source: Array<number> | null } => {
	const inputs = buildPreviewPlanInputsCfn(req);
	return {
		candidate: inputs.candidate.map((g) => g.width),
		source: inputs.source ? inputs.source.map((g) => g.width) : null
	};
};

/** The reference vocabulary of move_appliance_group, derived from a planned order. */
export const describeSlotCfn = (positions: Array<{ id: string; x: number }>, draggedId: string): { referenceId: string | null; position: 'left' | 'right' | null } => {
	const sorted = [...positions].sort((a, b) => a.x - b.x);
	const index = sorted.findIndex((p) => p.id === draggedId);
	if (index < 0) return { referenceId: null, position: null };
	if (index > 0) return { referenceId: sorted[index - 1].id, position: 'right' };
	if (sorted.length > 1) return { referenceId: sorted[1].id, position: 'left' };
	return { referenceId: null, position: null };
};

/**
 * Which drag plane to intersect: the one the view ray crosses more squarely.
 * `viewDotUp` and `viewDotOut` are |ray · normal| for the horizontal plane
 * (countertop) and the vertical plane (panel face).
 */
export const choosePlaneCfn = (viewDotUp: number, viewDotOut: number): 'horizontal' | 'vertical' => (viewDotUp >= viewDotOut ? 'horizontal' : 'vertical');
